"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import {
  ArrowRight,
  BarChart3,
  CheckCircle,
  CreditCard,
  LayoutDashboard,
  Users,
} from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TABS = [
  {
    icon: LayoutDashboard,
    color: "#171717",
    bg: "#F5F5F5",
    name: "General Dashboard",
    desc: "Default after sign-in — totals, Quick Actions, Top 5.",
  },
  {
    icon: BarChart3,
    color: "#1785B6",
    bg: "#EFF8FF",
    name: "Sales Dashboard",
    desc: "FY sales by state, customer, and item — not Analytics.",
  },
  {
    icon: CreditCard,
    color: "#7C3AED",
    bg: "#F5F3FF",
    name: "Credit Limit Dashboard",
    desc: "Customers / Vendors utilization and over-limit alerts.",
  },
  {
    icon: Users,
    color: "#059669",
    bg: "#ECFDF5",
    name: "Individual Work Tracking",
    desc: "Who created which sales and purchase documents.",
  },
  {
    icon: CheckCircle,
    color: "#DF5D01",
    bg: "#FFF7F2",
    name: "Getting Started",
    desc: "Eight steps that tick from live data — not a gate.",
  },
];

const GENERAL = [
  {
    label: "Filters",
    desc: "Date Range, Division, Branch. Apply / Reset / Lock",
  },
  {
    label: "Follows the date",
    desc: "Total Sales (invoices), Collection (Approved), Expense, Purchase (bills), Payments (Approved)",
  },
  {
    label: "Ignores the date",
    desc: "Customer Outstanding, Vendor Outstanding, Total Bank Balance, Due Amount",
  },
  {
    label: "Due Amount",
    desc: "Unpaid invoices past due date",
  },
  {
    label: "Vendor Due",
    desc: "Overdue bills — follows the date",
  },
  {
    label: "Bank Balance",
    desc: "Ignores Division and Branch as well",
  },
  {
    label: "Quick Actions",
    desc: "Create only — Invoice, Payment Received, Quote, Bill, Payment Made, Expense",
  },
  {
    label: "Top 5",
    desc: "Sales (invoices), Payment Received, Purchase (bills), Payment Made, Expenses — click a row to open it",
  },
];

const SALES = [
  {
    label: "Default date",
    desc: "Current Indian FY — 1 Apr to 31 Mar",
  },
  {
    label: "Filters",
    desc: "Date Range, Division → Branch, User (one person)",
  },
  {
    label: "KPI cards",
    desc: "Actual Sales (invoices, not cancelled), Active Customers, Total Collection, Outstanding, Due Amount",
  },
  {
    label: "By state",
    desc: "Sales by State / Sales Mix by State in ₹ crore — Top 5 or All",
  },
  {
    label: "Trend chart",
    desc: "Order - Invoice - Payment — own FY picker, not the main date",
  },
  {
    label: "Order status",
    desc: "Completed, Pending (Draft / Open / Partially Recorded), Cancelled",
  },
  {
    label: "Performance",
    desc: "State-wise (state → city → customer → invoices), Customer-wise, Item-wise",
  },
  {
    label: "No recent orders",
    desc: "Orange block — customers without orders in the last 90 days",
  },
];

const CREDIT = [
  {
    label: "Filters",
    desc: "None — no date, no division",
  },
  {
    label: "Toggle",
    desc: "Customers / Vendors in the header",
  },
  {
    label: "Summary cards",
    desc: "Total Credit Assigned, Outstanding, Portfolio Utilization, Available, Over-Limit Accounts, No Credit Limit Set",
  },
  {
    label: "Utilization grid",
    desc: "Number, name, limit, utilized, available (red if negative), bar, Risk, Last Payment",
  },
  {
    label: "Risk bands",
    desc: "Safe under 70%, Warning 70%, High 90%, Critical 100%+",
  },
  {
    label: "Over-Limit Alerts",
    desc: "Exceeded, Overdue Amt, Days Over, Latest Due — click a row for the party drawer",
  },
];

const TRACKING = [
  {
    label: "Default date",
    desc: "Today",
  },
  {
    label: "Filters",
    desc: "Date Range, Division → Branch",
  },
  {
    label: "Sales columns",
    desc: "Quotation, Sales Order, Delivery Challan, Invoice, Payment Received, Credit Note, Conversion %",
  },
  {
    label: "Conversion %",
    desc: "Invoice count / quotation count, capped at 100%",
  },
  {
    label: "Purchase columns",
    desc: "Purchase Order, GRPO, Bill, Payment Made, Debit Note",
  },
  {
    label: "Each cell",
    desc: "Amount plus document count — click to open that list for the user and date",
  },
  {
    label: "Total row",
    desc: "Sums the table",
  },
  {
    label: "No Activity",
    desc: "Company users with nothing in the period",
  },
];

const SETUP = [
  { n: "1", title: "Add Organisation Details", desc: "GSTIN or PAN, base currency, street / country / state / city" },
  { n: "2", title: "First item", desc: "All Master → Item" },
  { n: "3", title: "First customer", desc: "Sales → Customer" },
  { n: "4", title: "Setup banking", desc: "Primary bank on Banking → Banks" },
  { n: "5", title: "Invite your team", desc: "More than one user — Settings → Users" },
  { n: "6", title: "First invoice", desc: "Sales → Invoices" },
  { n: "7", title: "First vendor", desc: "Purchase → Vendors" },
  { n: "8", title: "First bill", desc: "Purchase → Bills" },
];

const RULES = [
  {
    topic: "Who",
    rule: "Home View. Title Dashboard. Not Analytics Dashboard, not Reports. No Export",
  },
  {
    topic: "Outstanding vs Due",
    rule: "Outstanding is party branch current balance. Due is overdue documents. They can disagree",
  },
  {
    topic: "What it affects",
    rule: "Nothing you post. Home only reads. Limits are Settings → Credit Limits",
  },
];

const sections = [
  { heading: "The five tabs" },
  { heading: "General Dashboard" },
  { heading: "Sales Dashboard" },
  { heading: "Credit Limit Dashboard" },
  { heading: "Individual Work Tracking" },
  { heading: "Getting Started" },
  { heading: "Business rules" },
];

function CardGrid({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((item) => (
        <div
          key={item.label}
          className="border border-[#E5E5E5] rounded-[12px] p-4 bg-white"
        >
          <p className="text-[14px] geist-medium text-[#111827] mb-1">
            {item.label}
          </p>
          <p className="text-[13px] geist-regular text-[#374151]">
            {item.desc}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function DashboardPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Dashboard
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone with Home View — live totals, credit watch, who
            raised what, and a setup checklist
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Left menu <B>Home</B>. On-screen only — not Analytics, not
            Reports.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-2 scroll-mt-24"
          >
            The five tabs
          </h2>
          <p className="text-[14px] geist-regular text-[#525252] mb-6">
            Default is General Dashboard.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <div
                  key={tab.name}
                  className="border border-[#E5E5E5] rounded-[12px] p-4 bg-white"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-8 h-8 rounded-[8px] flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: tab.bg }}
                    >
                      <Icon className="w-4 h-4" style={{ color: tab.color }} />
                    </div>
                    <h3 className="text-[15px] sentient-medium text-[#171717]">
                      {tab.name}
                    </h3>
                  </div>
                  <p className="text-[12px] geist-regular text-[#525252] leading-relaxed">
                    {tab.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            General Dashboard
          </h2>
          <CardGrid items={GENERAL} />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-2"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Sales Dashboard
          </h2>
          <CardGrid items={SALES} />
          <Callout type="note">
            Not Settings → Sales Target. Not Analytics Dashboard.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-3"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Credit Limit Dashboard
          </h2>
          <CardGrid items={CREDIT} />
          <Callout type="note">
            Limits are set on Settings → Credit Limits, or on the customer /
            vendor.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-4"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Individual Work Tracking
          </h2>
          <CardGrid items={TRACKING} />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-5"
            className="text-[24px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            Getting Started
          </h2>
          <p className="text-[15px] geist-regular text-[#374151] mb-4">
            Header: Let&apos;s get you up and running. Badge on the tab is
            remaining steps. Any order — ticks from live data.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SETUP.map((s) => (
              <div
                key={s.n}
                className="border border-[#E5E5E5] rounded-[12px] p-4 bg-white flex gap-3"
              >
                <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0">
                  {s.n}
                </span>
                <div>
                  <p className="text-[14px] geist-medium text-[#111827]">
                    {s.title}
                  </p>
                  <p className="text-[13px] geist-regular text-[#374151]">
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <Callout type="note">
            All set! when all eight are done. Admins who used CRM first may
            see Data Setup Required — a one-time backfill, hidden when remaining
            is 0.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-6"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/analytics/analytics-overview"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Analytics Dashboard <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The 28 named analytics — not these Home tabs
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/credit-limits"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Credit Limits <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where company / branch limits are set
            </p>
          </Link>
          <Link
            href="/documentation/books/getting-started/setting-up-your-account"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Setting Up Your Account <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Sign-up, trial, and the eight steps
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
